import type { CarrierOperator, CarrierTask, CarrierTrip } from "@/api/carrier";
import { StatusBadge } from "@/components/status-badge";
import {
  operatorDisplayName,
  operatorInitials as identityInitials,
} from "@/lib/operator-identity";
import type { TaskLane } from "./task-type-meta";
import { isDispatchTaskOverdue } from "./dispatch-read-model";

const CLOSED_TASK_STATUSES = new Set([
  "COMPLETED",
  "DONE",
  "CANCELLED",
  "CANCELED",
]);
const CLOSED_TRIP_STATUSES = new Set([
  "DONE",
  "CANCELLED",
  "CANCELED",
  "ARCHIVED",
]);

export function labelize(value: string | null | undefined): string {
  if (!value) return "-";
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ");
}

export function operatorName(op: CarrierOperator): string {
  return operatorDisplayName(op);
}

export function operatorInitials(op: CarrierOperator): string {
  return identityInitials(op);
}

export { taskLabel } from "@/lib/task-reference";

export function tripLabel(trip: CarrierTrip): string {
  return `TRP-${trip.tripNumber}`;
}

export function taskLocation(task: CarrierTask): string {
  const parts = [
    task.addressLine1 ?? task.address?.line1,
    task.addressCity ?? task.address?.city,
    task.addressProvince ?? task.address?.province,
  ].filter(Boolean);
  return parts.join(", ") || task.contactName || task.description || "-";
}

function taskLaneForCarrier(type: string): TaskLane {
  switch (type) {
    case "CUSTOMER_PICKUP":
    case "CARRIER_PICKUP":
      return "pickup";
    case "CUSTOMER_DELIVERY":
      return "delivery";
    case "CUSTOMER_RETURN":
      return "return";
    default:
      return "dropoff";
  }
}

export function taskCounts(tasks: CarrierTask[]): Record<TaskLane, number> {
  const counts = { pickup: 0, delivery: 0, dropoff: 0, return: 0 };
  for (const task of tasks) {
    counts[taskLaneForCarrier(task.type)] += 1;
  }
  return counts;
}

export function isOpenTask(task: CarrierTask): boolean {
  return !CLOSED_TASK_STATUSES.has(task.status.toUpperCase());
}

export function isOpenTrip(trip: CarrierTrip): boolean {
  return !CLOSED_TRIP_STATUSES.has(trip.status.toUpperCase());
}

export function isActiveTrip(trip: CarrierTrip): boolean {
  return trip.status.toUpperCase() === "ACTIVE";
}

export function isPlannedTrip(trip: CarrierTrip): boolean {
  return trip.status.toUpperCase() === "PLANNED";
}

export function isOverdue(task: CarrierTask, now = Date.now()): boolean {
  return isDispatchTaskOverdue(task, new Date(now));
}

export function hashColor(id: string): string {
  const palette = [
    "#4f46e5",
    "#0891b2",
    "#16a34a",
    "#d97706",
    "#db2777",
    "#7c3aed",
    "#0d9488",
  ];
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) & 0xffff;
  }
  return palette[h % palette.length];
}

export function StatusPill({ status }: { status: string }) {
  return <StatusBadge status={status} className="h-5 px-1.5 text-[10px]" />;
}

export function formatEta(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}
